import { useEffect, useId, useRef, useState } from 'react';
import { normalizeSocialUrlInput } from '@dukanti/shared';
import { useTranslation, type TranslationKey } from '@dukanti/shared/i18n';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { EXTERNAL_URL_IDLE_MS, externalUrlIssueKey } from './external-url-issue';

export interface ExternalUrlFieldProps {
  /** The committed URL (what the slot holds). */
  readonly url: string;
  /**
   * Every edit, normalized — valid or not (code review fix round 1). An
   * invalid or cleared URL reaches the dialog as an incomplete value: the
   * region schema refuses it, so it is never enqueued (RULE 0), the saved URL
   * stays until a valid URL or another destination is chosen, and closing
   * the dialog asks first. The field shows why it is refused.
   */
  readonly onDraftChange: (url: string) => void;
  /** A server message for the committed URL; shown only while the typed URL passes the client rule. */
  readonly serverIssueKey?: string;
  /** The merchant tried to finish: show the reason now, without waiting for blur or idle. */
  readonly forceReveal?: boolean;
  readonly testIdPrefix: string;
}

/**
 * The Instagram/Facebook URL input (PD1). What the merchant types is kept
 * here as typed; `normalizeSocialUrlInput` (trim, lowercase scheme and host)
 * runs before the check, so what is checked is what is stored. Every edit
 * leaves the field; the dialog's schema decides what is saved. The reason of
 * a refused URL (https, host, length, characters, empty) shows after blur or
 * a second of idle typing.
 */
export function ExternalUrlField({
  url,
  onDraftChange,
  serverIssueKey,
  forceReveal = false,
  testIdPrefix,
}: ExternalUrlFieldProps): React.JSX.Element {
  const { t } = useTranslation();
  const id = useId();
  const hintId = `${id}-hint`;
  const messageId = `${id}-message`;
  const [draft, setDraft] = useState(url);
  // A stored value opens with its message at once; an empty field waits.
  const [revealed, setRevealed] = useState(url !== '');
  const timer = useRef<number | null>(null);

  // A new committed value from outside (a reload, a rebase) replaces the draft.
  useEffect(() => {
    setDraft((current) => (normalizeSocialUrlInput(current) === url ? current : url));
  }, [url]);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  const normalized = normalizeSocialUrlInput(draft);
  const clientKey = externalUrlIssueKey(normalized);
  const shownKey: string | null =
    clientKey !== null ? (revealed || forceReveal ? clientKey : null) : (serverIssueKey ?? null);

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="block text-start">
        {t('admin.destination.externalLabel')}
      </Label>
      <Input
        id={id}
        type="url"
        dir="ltr"
        inputMode="url"
        autoComplete="off"
        spellCheck={false}
        value={draft}
        placeholder={t('admin.destination.externalPlaceholder')}
        aria-invalid={shownKey !== null ? true : undefined}
        aria-describedby={shownKey === null ? hintId : messageId}
        data-testid={`${testIdPrefix}-external-url`}
        onBlur={() => setRevealed(true)}
        onChange={(event) => {
          const raw = event.currentTarget.value;
          setDraft(raw);
          setRevealed(false);
          if (timer.current !== null) window.clearTimeout(timer.current);
          timer.current = window.setTimeout(() => setRevealed(true), EXTERNAL_URL_IDLE_MS);
          const next = normalizeSocialUrlInput(raw);
          if (next !== url) onDraftChange(next);
        }}
      />
      {/* The hint and a refusal would say the same thing twice (UI/UX P3-1): one at a time. */}
      {shownKey === null ? (
        <p id={hintId} className="text-muted-foreground text-start text-xs">
          {t('admin.destination.externalHint')}
        </p>
      ) : (
        <p
          id={messageId}
          role="alert"
          className="text-destructive text-start text-xs"
          data-testid={`${testIdPrefix}-external-url-error`}
        >
          {t(shownKey as TranslationKey)}
        </p>
      )}
    </div>
  );
}
